import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock } from 'lucide-react';

export function WarrantyTerms() {
  const terms = [
    { treatment: "Crowns & Bridges", years: "5-10 Years" },
    { treatment: "Fillings", years: "2 Years" },
    { treatment: "Dentures & Partials", years: "3 Years" },
    { treatment: "Implants", years: "5+ Years (case dependent)" },
    { treatment: "Veneers", years: "5 Years" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="bg-gradient-to-b from-white to-teal-50 py-16 md:py-24 px-6 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute top-0 right-0 text-teal-200 opacity-20 w-64 h-64" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="currentColor" d="M39.9,-65.7C53.4,-59.6,67.2,-51.6,75.1,-39.3C83,-27,85,-10.5,83.4,5.4C81.9,21.3,76.8,36.6,67.1,48.2C57.4,59.8,43.2,67.6,28.6,71.9C14,76.1,-1,76.7,-15.4,73.7C-29.8,70.7,-43.6,64,-55.1,53.4C-66.5,42.8,-75.6,28.3,-78.5,12.2C-81.4,-3.9,-78.2,-21.5,-70.2,-35.5C-62.3,-49.5,-49.6,-59.9,-36,-66.2C-22.3,-72.6,-7.8,-75,3.7,-81.4C15.2,-87.9,26.5,-71.9,39.9,-65.7Z" transform="translate(100 100)" />
        </svg>
        <svg className="absolute bottom-0 left-0 text-green-200 opacity-20 w-96 h-96" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="currentColor" d="M46.5,-78.3C59.1,-71.9,67.8,-58.1,74.2,-43.7C80.5,-29.3,84.5,-14.7,83.2,-0.8C81.9,13.1,75.3,26.2,67.7,38.9C60.1,51.6,51.4,63.9,39.7,70.4C28,77,14,77.7,-0.2,77.9C-14.3,78.2,-28.7,78,-42.5,73C-56.4,68,-69.7,58.2,-76.4,44.9C-83.2,31.5,-83.4,14.8,-80.1,0C-76.9,-14.7,-70.2,-29.4,-61.1,-41.1C-52.1,-52.8,-40.7,-61.4,-28.5,-68C-16.3,-74.6,-3.1,-79.1,9.7,-94.4C22.4,-109.7,34,-84.6,46.5,-78.3Z" transform="translate(100 100)" />
        </svg>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-green-500 flex items-center justify-center">
              <Shield size={16} className="text-white" />
            </div>
            <div className="h-px w-12 bg-gradient-to-r from-teal-500 to-green-500"></div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-green-500 flex items-center justify-center">
              <Clock size={16} className="text-white" />
            </div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Warranty 
            <span className="bg-gradient-to-r from-teal-600 to-green-600 text-transparent bg-clip-text"> Timeframes </span> 
            by Treatment
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We stand behind our work with these comprehensive warranty periods, ensuring your dental investment is protected for years to come.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {terms.map((term, i) => (
            <motion.div 
              key={i} 
              className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-teal-500 hover:shadow-xl transition-shadow"
              variants={itemVariants}
              whileHover={{ 
                x: 5,
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800">{term.treatment}</h4>
                  <p className="text-gray-500 mt-1">Coverage Period</p>
                </div>
                <div className="bg-gradient-to-r from-teal-500 to-green-500 text-white font-bold px-4 py-2 rounded-full text-sm">
                  {term.years}
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-4 h-4 mr-2 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  100% coverage during warranty period
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="mt-12 text-center bg-gradient-to-r from-teal-50 to-green-50 p-6 rounded-xl border border-teal-100"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Important Note</h3>
          <p className="text-gray-600">
            All warranties are subject to proper home care and regular check-ups. 
            Please consult with your dentist for specific details and conditions.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export default WarrantyTerms;